import {
  faCalendarWeek,
  faClock,
  faFileCertificate,
  faMessagesQuestion,
  faPeople,
  faScaleUnbalanced,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'

import { DIALOGUE_CLUB_TEMPLATE_SLUG, DIALOGUE_EVENT_TEMPLATE_SLUG } from '@/queries/constants'
import type {
  ProgramDetails,
  ProgramFactoid,
  ProgramIteration,
  ProgramMarketingConfig,
  ProgramOfferingTemplate,
} from '@/client/services/programs/types'
import { getPublicUrl } from '@/client/services/url'
import { DIALOGUES_WORLD_SLUG } from '@/client/services/worlds/constants'

import {
  DIALOGUE_CLUB_OFFERING_SLUG,
  DIALOGUE_EVENT_OFFERING_SLUG,
  type DialoguesIterationSlug,
} from './constants'

export const DIALOGUE_EVENT_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: DIALOGUE_EVENT_OFFERING_SLUG,
  title: 'Dialogues One-Time Event',
  learnerDescription:
    "Have a 1-on-1 conversation with a peer from around the world on a topic you choose. We'll provide you with a discussion guide to help steer the conversation.",
  sessionLength: 60,
  imageUrl: getPublicUrl('programs/offerings/i-dialogues-topics.png'),
  wizardIcon: faPeople,
}

export const DIALOGUE_CLUB_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: DIALOGUE_CLUB_OFFERING_SLUG,
  title: 'Dialogues Club',
  learnerDescription:
    "Meet every other week in a virtual club with students around the world. You'll make new friends and stand out with a unique extracurricular opportunity colleges are looking for.",
  sessionLength: 60,
  sessionFrequency: {
    every: 2,
    per: 'weeks',
  },
  imageUrl: getPublicUrl('programs/offerings/i-dialogues-clubs-2.png'),
  wizardIcon: faScaleUnbalanced,
}

export const DIALOGUES_PROGRAM_DETAILS: ProgramDetails = {
  slug: 'dialogues',
  title: 'Dialogues',
  descriptionShort: 'Join 1-on-1 conversations with students from across the globe',
  description: 'Join 1-on-1 conversations that truly matter with students from across the globe',
  worldSlug: DIALOGUES_WORLD_SLUG,
  programType: 'dialogue',
  learnerWaitlistName: 'dialogues-learner',
  tutorWaitlistName: 'dialogues-tutor',
}

export const DIALOGUES_ITERATIONS: Record<DialoguesIterationSlug, ProgramIteration> = {
  'always-open': {
    iterationSlug: 'always-open',
    title: 'Always Open',
    learnerRegistrationOpens: DateTime.local(2025, 8, 1, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2099, 1, 31, 23, 59, { zone: 'America/New_York' }), // Dialogues doesn't have iterations, so this one is open for many years.
    tutorRegistrationOpens: DateTime.local(2025, 8, 1, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2026, 1, 1, 23, 59, { zone: 'America/New_York' }), // Tutor registration deadline in the past so that it doesn't show an incorrect deadline on /tutor.
    startDate: DateTime.local(2025, 8, 1, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2026, 1, 31, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: DIALOGUE_EVENT_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 8, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 1, 31, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: DIALOGUE_EVENT_TEMPLATE_SLUG,
      },
      {
        offeringTemplate: DIALOGUE_CLUB_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 8, 1, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2026, 1, 31, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: DIALOGUE_CLUB_TEMPLATE_SLUG,
      },
    ],
  },
}

const DIALOGUES_PROGRAM_FACTOIDS: ProgramFactoid[] = [
  {
    key: 'session-duration',
    text: '60 minutes',
    subtext: 'Per conversation',
    iconProps: { size: 'md', color: 'yellow600', faIcon: faClock },
  },
  {
    key: 'format',
    text: '1-on-1',
    subtext: 'With a peer',
    iconProps: { size: 'md', color: 'yellow600', faIcon: faUserGroup },
  },
  {
    key: 'clubs-frequency',
    text: 'Twice a month',
    subtext: 'Clubs meet every other week',
    iconProps: { size: 'md', color: 'yellow600', faIcon: faCalendarWeek },
  },
]

export const DIALOGUES_MARKETING_CONFIG: ProgramMarketingConfig = {
  header: {
    learnerText: 'Join 1-on-1 conversations with students from across the globe',
    learnerSubtext:
      'Explore meaningful topics, hear new perspectives, and build your Dialogues portfolio.',
    tutorText: 'Help build community through Dialogues events and clubs',
    tutorSubtext: 'Facilitate thoughtful conversations and support peers in exploring new ideas.',
    backgroundImageUrl: 'dialogues/Dialogues-Waves-Background-v4.svg',
    backgroundColor: 'yellow400',
    factoids: DIALOGUES_PROGRAM_FACTOIDS,
    partnerships: [],
  },
  whatYoullDo: {
    iconProps: { color: 'yellow600' },
    bulletsLearner: [
      {
        key: 'join-conversations',
        icon: faMessagesQuestion,
        body: 'Join a 1-on-1 Zoom conversation on topics you care about',
      },
      {
        key: 'explore-viewpoints',
        icon: faScaleUnbalanced,
        body: 'Explore different viewpoints with empathy and curiosity',
      },
      {
        key: 'build-portfolio',
        icon: faFileCertificate,
        body: 'Build a Dialogues portfolio you can share with colleges',
      },
      {
        key: 'join-club',
        icon: faCalendarWeek,
        body: 'Join a Dialogues Club that meets every other week',
      },
    ],
    bulletsTutor: [
      {
        key: 'facilitate',
        icon: faUserGroup,
        body: 'Facilitate thoughtful conversations and help peers feel heard',
      },
      {
        key: 'community',
        icon: faPeople,
        body: 'Build a welcoming community through Dialogues events and clubs',
      },
      {
        key: 'recognition',
        icon: faFileCertificate,
        body: 'Earn leadership experience and recognition for service',
      },
    ],
  },
  learnerBenefits: [
    {
      id: 'meaningful-conversation',
      text: 'Have meaningful 1-on-1 conversations on topics you choose',
      faIcon: faMessagesQuestion,
      iconProps: { color: 'yellow600', size: 'lg' },
    },
    {
      id: 'perspective-taking',
      text: 'Develop empathy and open-mindedness by exploring different viewpoints',
      faIcon: faScaleUnbalanced,
      iconProps: { color: 'pink500', size: 'lg' },
    },
    {
      id: 'portfolio',
      text: 'Showcase your growth with a Dialogues portfolio for college applications',
      faIcon: faFileCertificate,
      iconProps: { color: 'purple500', size: 'lg' },
    },
  ],
  tutorBenefits: [
    {
      id: 'leadership',
      text: 'Gain leadership experience by facilitating clubs and events',
      faIcon: faUserGroup,
      iconProps: { color: 'green600', size: 'lg' },
    },
    {
      id: 'service',
      text: 'Give back by creating spaces for respectful conversation',
      faIcon: faPeople,
      iconProps: { color: 'blue600', size: 'lg' },
    },
    {
      id: 'recognition',
      text: 'Earn recognition that can strengthen your extracurricular profile',
      faIcon: faFileCertificate,
      iconProps: { color: 'yellow700', size: 'lg' },
    },
  ],
  learnerSignUpCard: {
    signUpCardHeading: 'Explore Sessions',
    signUpCardText: 'Join a 1-on-1 conversation or a club that meets every other week.',
  },
  tutorSignUpCard: {
    signUpCardHeading: 'Lead a Dialogues Club',
    signUpCardText: 'Facilitate meaningful conversations and help build a global community.',
  },
  programFactoids: DIALOGUES_PROGRAM_FACTOIDS,
}
